import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { API_ENDPOINTS } from '../config/api';

const Games = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const student = location.state?.student;

  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedGame, setSelectedGame] = useState(null);

  useEffect(() => {
    if (student) {
      fetchGames();
    }
  }, [student]);

  const fetchGames = async () => {
    try {
      const response = await fetch(`${API_ENDPOINTS.BASE_URL}/api/games?class=${encodeURIComponent(student.className)}`);
      const gamesData = await response.json();
      setGames(gamesData);
    } catch (error) {
      console.error('Error fetching games:', error);
      setError('Failed to load games. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handlePlayGame = (game) => {
    setSelectedGame(game);
  };

  const handleCloseGame = () => {
    setSelectedGame(null);
  };

  const renderGameComponent = (game) => {
    switch (game.componentName) {
      case 'ColorMatchingGame':
        return <ColorMatchingGame onClose={handleCloseGame} />;
      case 'ShapeSorterGame':
        return <ShapeSorterGame onClose={handleCloseGame} />;
      case 'NumberCountingGame':
        return <NumberCountingGame onClose={handleCloseGame} />;
      case 'SimpleAdditionGame':
        return <SimpleAdditionGame onClose={handleCloseGame} />;
      case 'WordBuilderGame':
        return <WordBuilderGame onClose={handleCloseGame} />;
      case 'MathQuizGame':
        return <MathQuizGame onClose={handleCloseGame} />;
      case 'RiddleSolverGame':
        return <RiddleSolverGame onClose={handleCloseGame} />;
      case 'LogicGridGame':
        return <LogicGridGame onClose={handleCloseGame} />;
      case 'SubtractionAdventureGame':
        return <SubtractionAdventureGame onClose={handleCloseGame} />;
      case 'MultiplicationMasterGame':
        return <MultiplicationMasterGame onClose={handleCloseGame} />;
      case 'DivisionChallengeGame':
        return <DivisionChallengeGame onClose={handleCloseGame} />;
      case 'FractionFunGame':
        return <FractionFunGame onClose={handleCloseGame} />;
      case 'ShapeAreaCalculatorGame':
        return <ShapeAreaCalculatorGame onClose={handleCloseGame} />;
      case 'TimeTellerGame':
        return <TimeTellerGame onClose={handleCloseGame} />;
      case 'SpellingBeeGame':
        return <SpellingBeeGame onClose={handleCloseGame} />;
      case 'VocabularyBuilderGame':
        return <VocabularyBuilderGame onClose={handleCloseGame} />;
      case 'ReadingComprehensionGame':
        return <ReadingComprehensionGame onClose={handleCloseGame} />;
      case 'RhymingWordsGame':
        return <RhymingWordsGame onClose={handleCloseGame} />;
      case 'SentenceBuilderGame':
        return <SentenceBuilderGame onClose={handleCloseGame} />;
      case 'PatternRecognitionGame':
        return <PatternRecognitionGame onClose={handleCloseGame} />;
      case 'SequenceSolverGame':
        return <SequenceSolverGame onClose={handleCloseGame} />;
      case 'MazeNavigatorGame':
        return <MazeNavigatorGame onClose={handleCloseGame} />;
      case 'CodeBreakerGame':
        return <CodeBreakerGame onClose={handleCloseGame} />;
      case 'MemoryMatchGame':
        return <MemoryMatchGame onClose={handleCloseGame} />;
      case 'AnimalSoundsGame':
        return <AnimalSoundsGame onClose={handleCloseGame} />;
      case 'ColorMixerGame':
        return <ColorMixerGame onClose={handleCloseGame} />;
      case 'SoundRecognitionGame':
        return <SoundRecognitionGame onClose={handleCloseGame} />;
      case 'PlanetExplorerGame':
        return <PlanetExplorerGame onClose={handleCloseGame} />;
      case 'WeatherWatcherGame':
        return <WeatherWatcherGame onClose={handleCloseGame} />;
      case 'LivingNonlivingGame':
        return <LivingNonlivingGame onClose={handleCloseGame} />;
      default:
        return <div>Game not available</div>;
    }
  };

  if (!student) {
    return (
      <div className="min-h-screen bg-blue-50 p-8">
        <div className="text-center">
          <p className="text-red-600">Student information not found. Please log in again.</p>
          <button
            onClick={() => navigate('/student-login')}
            className="mt-4 bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-blue-50 p-8">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading games...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-blue-50 p-8">
        <div className="text-center">
          <p className="text-red-600">{error}</p>
          <button
            onClick={fetchGames}
            className="mt-4 bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-blue-50 p-8">
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-bold text-blue-900">Educational Games</h1>
        <button
          onClick={() => navigate('/student-dashboard', { state: { student } })}
          className="bg-gray-600 text-white px-4 py-2 rounded hover:bg-gray-700 transition duration-300"
        >
          Back to Dashboard
        </button>
      </div>

      <div className="mb-6">
        <p className="text-gray-600">
          Welcome {student.firstName}! Here are some fun educational games suitable for your class ({student.className}).
        </p>
      </div>

      {games.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-gray-600">No games available for your class at the moment.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {games.map((game) => (
            <div
              key={game.id}
              className="bg-white rounded-2xl shadow-lg p-6 border border-blue-100 hover:shadow-2xl transition duration-300"
            >
              <div className="flex items-center justify-between mb-4">
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                  game.difficulty === 'Easy' ? 'bg-green-100 text-green-800' :
                  game.difficulty === 'Medium' ? 'bg-yellow-100 text-yellow-800' :
                  'bg-red-100 text-red-800'
                }`}>
                  {game.difficulty}
                </span>
                <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                  {game.category}
                </span>
              </div>

              <h3 className="text-xl font-semibold text-blue-800 mb-2">{game.title}</h3>
              <p className="text-gray-600 mb-4">{game.description}</p>

              <button
                onClick={() => handlePlayGame(game)}
                className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition duration-300 font-medium"
              >
                Play Game
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Game Modal */}
      {selectedGame && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
            <div className="p-4 border-b border-gray-200 flex justify-between items-center">
              <h2 className="text-xl font-bold text-gray-900">{selectedGame.title}</h2>
              <button
                onClick={handleCloseGame}
                className="text-gray-500 hover:text-gray-700 text-2xl font-bold"
              >
                ×
              </button>
            </div>
            <div className="p-4 overflow-y-auto max-h-[calc(90vh-80px)]">
              {renderGameComponent(selectedGame)}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Game Components
const ColorMatchingGame = ({ onClose }) => {
  const [colors, setColors] = useState(['red', 'blue', 'green', 'yellow', 'purple', 'orange']);
  const [targetColor, setTargetColor] = useState('');
  const [score, setScore] = useState(0);
  const [message, setMessage] = useState('');

  useEffect(() => {
    startNewRound();
  }, []);

  const startNewRound = () => {
    const randomColor = colors[Math.floor(Math.random() * colors.length)];
    setTargetColor(randomColor);
    setMessage('');
  };

  const handleColorClick = (color) => {
    if (color === targetColor) {
      setScore(score + 1);
      setMessage('Correct! Success');
      setTimeout(startNewRound, 1500);
    } else {
      setMessage('Try again! 😊');
    }
  };

  return (
    <div className="text-center p-8">
      <h3 className="text-2xl font-bold mb-4">Color Matching Game</h3>
      <p className="mb-6">Click on the <span className="font-bold" style={{color: targetColor}}>{targetColor}</span> circle!</p>

      <div className="grid grid-cols-3 gap-4 mb-6">
        {colors.map((color) => (
          <button
            key={color}
            onClick={() => handleColorClick(color)}
            className="w-20 h-20 rounded-full border-4 border-gray-300 hover:border-gray-500 transition duration-300"
            style={{ backgroundColor: color }}
            aria-label={`Click ${color} circle`}
          />
        ))}
      </div>

      <p className="text-lg mb-4">{message}</p>
      <p className="text-xl font-semibold">Score: {score}</p>
    </div>
  );
};

const ShapeSorterGame = ({ onClose }) => {
  const [shapes, setShapes] = useState([
    { id: 1, type: 'circle', color: 'red' },
    { id: 2, type: 'square', color: 'blue' },
    { id: 3, type: 'triangle', color: 'green' },
    { id: 4, type: 'circle', color: 'yellow' },
    { id: 5, type: 'square', color: 'purple' },
    { id: 6, type: 'triangle', color: 'orange' }
  ]);
  const [targetShape, setTargetShape] = useState('');
  const [score, setScore] = useState(0);
  const [message, setMessage] = useState('');

  useEffect(() => {
    startNewRound();
  }, []);

  const startNewRound = () => {
    const shapeTypes = ['circle', 'square', 'triangle'];
    const randomShape = shapeTypes[Math.floor(Math.random() * shapeTypes.length)];
    setTargetShape(randomShape);
    setMessage('');
  };

  const handleShapeClick = (shape) => {
    if (shape.type === targetShape) {
      setScore(score + 1);
      setMessage('Correct! Success');
      setTimeout(startNewRound, 1500);
    } else {
      setMessage('Try again! 😊');
    }
  };

  const renderShape = (shape) => {
    const baseClasses = "w-16 h-16 border-4 border-gray-300 hover:border-gray-500 transition duration-300 cursor-pointer mx-auto";
    switch (shape.type) {
      case 'circle':
        return <div className={`${baseClasses} rounded-full`} style={{ backgroundColor: shape.color }} />;
      case 'square':
        return <div className={`${baseClasses}`} style={{ backgroundColor: shape.color }} />;
      case 'triangle':
        return (
          <div className="w-16 h-16 mx-auto cursor-pointer" onClick={() => handleShapeClick(shape)}>
            <div
              className="w-0 h-0 border-l-8 border-r-8 border-b-16 border-l-transparent border-r-transparent border-b-gray-300 hover:border-b-gray-500 transition duration-300 mx-auto"
              style={{ borderBottomColor: shape.color }}
            />
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="text-center p-8">
      <h3 className="text-2xl font-bold mb-4">Shape Sorter</h3>
      <p className="mb-6">Click on all the <span className="font-bold">{targetShape}s</span>!</p>

      <div className="grid grid-cols-3 gap-6 mb-6">
        {shapes.map((shape) => (
          <div key={shape.id} onClick={() => handleShapeClick(shape)}>
            {renderShape(shape)}
          </div>
        ))}
      </div>

      <p className="text-lg mb-4">{message}</p>
      <p className="text-xl font-semibold">Score: {score}</p>
    </div>
  );
};

const NumberCountingGame = ({ onClose }) => {
  const [objects, setObjects] = useState([]);
  const [correctCount, setCorrectCount] = useState(0);
  const [userAnswer, setUserAnswer] = useState('');
  const [message, setMessage] = useState('');
  const [score, setScore] = useState(0);

  useEffect(() => {
    generateNewQuestion();
  }, []);

  const generateNewQuestion = () => {
    const count = Math.floor(Math.random() * 10) + 1;
    setCorrectCount(count);
    setObjects(Array.from({ length: count }, (_, i) => i));
    setUserAnswer('');
    setMessage('');
  };

  const handleSubmit = () => {
    const answer = parseInt(userAnswer);
    if (answer === correctCount) {
      setScore(score + 1);
      setMessage('Correct! Success');
      setTimeout(generateNewQuestion, 1500);
    } else {
      setMessage('Try again! 😊');
    }
  };

  return (
    <div className="text-center p-8">
      <h3 className="text-2xl font-bold mb-4">Number Counting Adventure</h3>
      <p className="mb-6">Count the stars and type the number!</p>

      <div className="mb-6">
        {objects.map((obj) => (
          <span key={obj} className="text-4xl mx-1">⭐</span>
        ))}
      </div>

      <div className="mb-4">
        <input
          type="number"
          value={userAnswer}
          onChange={(e) => setUserAnswer(e.target.value)}
          className="w-20 px-3 py-2 border border-gray-300 rounded-md text-center text-xl"
          placeholder="?"
        />
      </div>

      <button
        onClick={handleSubmit}
        className="bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700 mb-4"
      >
        Submit
      </button>

      <p className="text-lg mb-4">{message}</p>
      <p className="text-xl font-semibold">Score: {score}</p>
    </div>
  );
};

const SimpleAdditionGame = ({ onClose }) => {
  const [num1, setNum1] = useState(0);
  const [num2, setNum2] = useState(0);
  const [userAnswer, setUserAnswer] = useState('');
  const [message, setMessage] = useState('');
  const [score, setScore] = useState(0);

  useEffect(() => {
    generateNewQuestion();
  }, []);

  const generateNewQuestion = () => {
    const a = Math.floor(Math.random() * 10) + 1;
    const b = Math.floor(Math.random() * 10) + 1;
    setNum1(a);
    setNum2(b);
    setUserAnswer('');
    setMessage('');
  };

  const handleSubmit = () => {
    const answer = parseInt(userAnswer);
    const correct = num1 + num2;
    if (answer === correct) {
      setScore(score + 1);
      setMessage('Correct! Success');
      setTimeout(generateNewQuestion, 1500);
    } else {
      setMessage('Try again! 😊');
    }
  };

  return (
    <div className="text-center p-8">
      <h3 className="text-2xl font-bold mb-4">Simple Addition</h3>
      <p className="mb-6">Solve the addition problem!</p>

      <div className="text-4xl mb-6">
        <span>{num1}</span> + <span>{num2}</span> = <span className="text-blue-600">Loading</span>
      </div>

      <div className="mb-4">
        <input
          type="number"
          value={userAnswer}
          onChange={(e) => setUserAnswer(e.target.value)}
          className="w-20 px-3 py-2 border border-gray-300 rounded-md text-center text-xl"
          placeholder="?"
        />
      </div>

      <button
        onClick={handleSubmit}
        className="bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700 mb-4"
      >
        Submit
      </button>

      <p className="text-lg mb-4">{message}</p>
      <p className="text-xl font-semibold">Score: {score}</p>
    </div>
  );
};

const WordBuilderGame = ({ onClose }) => {
  const words = ['CAT', 'DOG', 'SUN', 'CAR', 'BOOK', 'TREE', 'BALL', 'HOUSE'];
  const [currentWord, setCurrentWord] = useState('');
  const [scrambledWord, setScrambledWord] = useState('');
  const [userAnswer, setUserAnswer] = useState('');
  const [message, setMessage] = useState('');
  const [score, setScore] = useState(0);

  useEffect(() => {
    generateNewWord();
  }, []);

  const generateNewWord = () => {
    const word = words[Math.floor(Math.random() * words.length)];
    const scrambled = word.split('').sort(() => Math.random() - 0.5).join('');
    setCurrentWord(word);
    setScrambledWord(scrambled);
    setUserAnswer('');
    setMessage('');
  };

  const handleSubmit = () => {
    if (userAnswer.toUpperCase() === currentWord) {
      setScore(score + 1);
      setMessage('Correct! Success');
      setTimeout(generateNewWord, 1500);
    } else {
      setMessage('Try again! 😊');
    }
  };

  return (
    <div className="text-center p-8">
      <h3 className="text-2xl font-bold mb-4">Word Builder</h3>
      <p className="mb-6">Unscramble the letters to form a word!</p>

      <div className="text-4xl mb-6 font-mono tracking-wider">
        {scrambledWord.split('').map((letter, index) => (
          <span key={index} className="inline-block mx-1 p-2 bg-blue-100 rounded">
            {letter}
          </span>
        ))}
      </div>

      <div className="mb-4">
        <input
          type="text"
          value={userAnswer}
          onChange={(e) => setUserAnswer(e.target.value)}
          className="w-32 px-3 py-2 border border-gray-300 rounded-md text-center text-xl uppercase"
          placeholder="Word"
          maxLength={scrambledWord.length}
        />
      </div>

      <button
        onClick={handleSubmit}
        className="bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700 mb-4"
      >
        Submit
      </button>

      <p className="text-lg mb-4">{message}</p>
      <p className="text-xl font-semibold">Score: {score}</p>
    </div>
  );
};

const MathQuizGame = ({ onClose }) => {
  const [question, setQuestion] = useState('');
  const [correctAnswer, setCorrectAnswer] = useState(0);
  const [userAnswer, setUserAnswer] = useState('');
  const [message, setMessage] = useState('');
  const [score, setScore] = useState(0);
  const [timeLeft, setTimeLeft] = useState(30);

  useEffect(() => {
    generateNewQuestion();
  }, []);

  useEffect(() => {
    if (timeLeft > 0) {
      const timer = setTimeout(() => setTimeLeft(timeLeft - 1), 1000);
      return () => clearTimeout(timer);
    } else {
      setMessage('Time\'s up! Try again.');
      setTimeout(generateNewQuestion, 1500);
    }
  }, [timeLeft]);

  const generateNewQuestion = () => {
    const operations = ['+', '-', '*'];
    const op = operations[Math.floor(Math.random() * operations.length)];
    let a, b, answer;

    switch (op) {
      case '+':
        a = Math.floor(Math.random() * 20) + 1;
        b = Math.floor(Math.random() * 20) + 1;
        answer = a + b;
        break;
      case '-':
        a = Math.floor(Math.random() * 20) + 10;
        b = Math.floor(Math.random() * 10) + 1;
        answer = a - b;
        break;
      case '*':
        a = Math.floor(Math.random() * 10) + 1;
        b = Math.floor(Math.random() * 10) + 1;
        answer = a * b;
        break;
      default:
        answer = 0;
    }

    setQuestion(`${a} ${op} ${b}`);
    setCorrectAnswer(answer);
    setUserAnswer('');
    setMessage('');
    setTimeLeft(30);
  };

  const handleSubmit = () => {
    const answer = parseInt(userAnswer);
    if (answer === correctAnswer) {
      setScore(score + 1);
      setMessage('Correct! Success');
      setTimeout(generateNewQuestion, 1500);
    } else {
      setMessage('Try again! 😊');
    }
  };

  return (
    <div className="text-center p-8">
      <h3 className="text-2xl font-bold mb-4">Math Quiz Challenge</h3>
      <p className="mb-4">Solve as fast as you can!</p>

      <div className="text-4xl mb-6 font-mono">
        {question} = <span className="text-blue-600">Loading</span>
      </div>

      <div className="mb-4">
        <input
          type="number"
          value={userAnswer}
          onChange={(e) => setUserAnswer(e.target.value)}
          className="w-24 px-3 py-2 border border-gray-300 rounded-md text-center text-xl"
          placeholder="?"
        />
      </div>

      <div className="mb-4">
        <div className="text-lg font-semibold">Time: {timeLeft}s</div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div
            className="bg-blue-600 h-2 rounded-full transition-all duration-1000"
            style={{ width: `${(timeLeft / 30) * 100}%` }}
          />
        </div>
      </div>

      <button
        onClick={handleSubmit}
        className="bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700 mb-4"
      >
        Submit
      </button>

      <p className="text-lg mb-4">{message}</p>
      <p className="text-xl font-semibold">Score: {score}</p>
    </div>
  );
};

const RiddleSolverGame = ({ onClose }) => {
  const riddles = [
    { question: "What has keys but can't open locks?", answer: "PIANO" },
    { question: "What comes once in a minute, twice in a moment, but never in a thousand years?", answer: "M" },
    { question: "I speak without a mouth and hear without ears. I have no body, but I come alive with the wind. What am I?", answer: "ECHO" },
    { question: "The more you take, the more you leave behind. What am I?", answer: "FOOTSTEPS" },
    { question: "What has a head, a tail, is brown, and has no legs?", answer: "PENNY" }
  ];

  const [currentRiddle, setCurrentRiddle] = useState({});
  const [userAnswer, setUserAnswer] = useState('');
  const [message, setMessage] = useState('');
  const [score, setScore] = useState(0);
  const [showAnswer, setShowAnswer] = useState(false);

  useEffect(() => {
    generateNewRiddle();
  }, []);

  const generateNewRiddle = () => {
    const riddle = riddles[Math.floor(Math.random() * riddles.length)];
    setCurrentRiddle(riddle);
    setUserAnswer('');
    setMessage('');
    setShowAnswer(false);
  };

  const handleSubmit = () => {
    if (userAnswer.toUpperCase() === currentRiddle.answer) {
      setScore(score + 1);
      setMessage('Correct! Success');
      setTimeout(generateNewRiddle, 2000);
    } else {
      setMessage('Not quite! Try again or click "Show Answer" for help.');
    }
  };

  return (
    <div className="text-center p-8">
      <h3 className="text-2xl font-bold mb-4">Riddle Master</h3>
      <p className="mb-6 italic text-lg">{currentRiddle.question}</p>

      <div className="mb-4">
        <input
          type="text"
          value={userAnswer}
          onChange={(e) => setUserAnswer(e.target.value)}
          className="w-64 px-3 py-2 border border-gray-300 rounded-md text-center text-xl uppercase"
          placeholder="Your answer"
        />
      </div>

      <div className="mb-4">
        <button
          onClick={handleSubmit}
          className="bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700 mr-2"
        >
          Submit
        </button>
        <button
          onClick={() => setShowAnswer(!showAnswer)}
          className="bg-gray-600 text-white px-6 py-2 rounded hover:bg-gray-700"
        >
          {showAnswer ? 'Hide' : 'Show'} Answer
        </button>
      </div>

      {showAnswer && (
        <p className="mb-4 text-green-600 font-semibold">Answer: {currentRiddle.answer}</p>
      )}

      <p className="text-lg mb-4">{message}</p>
      <p className="text-xl font-semibold">Score: {score}</p>
    </div>
  );
};

const LogicGridGame = ({ onClose }) => {
  const [grid, setGrid] = useState([
    [false, false, false],
    [false, false, false],
    [false, false, false]
  ]);
  const [message, setMessage] = useState('');
  const [score, setScore] = useState(0);

  const toggleCell = (row, col) => {
    const newGrid = grid.map((r, i) =>
      r.map((c, j) => (i === row && j === col ? !c : c))
    );
    setGrid(newGrid);

    // Check for win condition (any row, column, or diagonal filled)
    const isWin = checkWin(newGrid);
    if (isWin) {
      setScore(score + 1);
      setMessage('Well done! Success');
      setTimeout(() => {
        setGrid([
          [false, false, false],
          [false, false, false],
          [false, false, false]
        ]);
        setMessage('');
      }, 1500);
    }
  };

  const checkWin = (grid) => {
    // Check rows
    for (let i = 0; i < 3; i++) {
      if (grid[i].every(cell => cell)) return true;
    }
    // Check columns
    for (let j = 0; j < 3; j++) {
      if (grid.every(row => row[j])) return true;
    }
    // Check diagonals
    if (grid[0][0] && grid[1][1] && grid[2][2]) return true;
    if (grid[0][2] && grid[1][1] && grid[2][0]) return true;
    return false;
  };

  return (
    <div className="text-center p-8">
      <h3 className="text-2xl font-bold mb-4">Logic Grid Puzzle</h3>
      <p className="mb-6">Click cells to fill rows, columns, or diagonals!</p>

      <div className="inline-block mb-6">
        {grid.map((row, rowIndex) => (
          <div key={rowIndex} className="flex">
            {row.map((cell, colIndex) => (
              <button
                key={colIndex}
                onClick={() => toggleCell(rowIndex, colIndex)}
                className={`w-16 h-16 border-2 border-gray-400 m-1 transition duration-300 ${
                  cell ? 'bg-blue-600' : 'bg-white hover:bg-gray-100'
                }`}
              />
            ))}
          </div>
        ))}
      </div>

      <p className="text-lg mb-4">{message}</p>
      <p className="text-xl font-semibold">Score: {score}</p>
    </div>
  );
};

const SubtractionAdventureGame = ({ onClose }) => {
  const [num1, setNum1] = useState(0);
  const [num2, setNum2] = useState(0);
  const [userAnswer, setUserAnswer] = useState('');
  const [message, setMessage] = useState('');
  const [score, setScore] = useState(0);

  useEffect(() => {
    generateNewQuestion();
  }, []);

  const generateNewQuestion = () => {
    const a = Math.floor(Math.random() * 20) + 10;
    const b = Math.floor(Math.random() * 9) + 1;
    setNum1(a);
    setNum2(b);
    setUserAnswer('');
    setMessage('');
  };

  const handleSubmit = () => {
    const answer = parseInt(userAnswer);
    const correct = num1 - num2;
    if (answer === correct) {
      setScore(score + 1);
      setMessage('Correct! Success');
      setTimeout(generateNewQuestion, 1500);
    } else {
      setMessage('Try again! 😊');
    }
  };

  return (
    <div className="text-center p-8">
      <h3 className="text-2xl font-bold mb-4">Subtraction Adventure</h3>
      <p className="mb-6">Take away the numbers!</p>

      <div className="text-4xl mb-6">
        <span>{num1}</span> - <span>{num2}</span> = <span className="text-blue-600">Loading</span>
      </div>

      <div className="mb-4">
        <input
          type="number"
          value={userAnswer}
          onChange={(e) => setUserAnswer(e.target.value)}
          className="w-20 px-3 py-2 border border-gray-300 rounded-md text-center text-xl"
          placeholder="?"
        />
      </div>

      <button
        onClick={handleSubmit}
        className="bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700 mb-4"
      >
        Submit
      </button>

      <p className="text-lg mb-4">{message}</p>
      <p className="text-xl font-semibold">Score: {score}</p>
    </div>
  );
};

const MultiplicationMasterGame = ({ onClose }) => {
  const [num1, setNum1] = useState(0);
  const [num2, setNum2] = useState(0);
  const [userAnswer, setUserAnswer] = useState('');
  const [message, setMessage] = useState('');
  const [score, setScore] = useState(0);

  useEffect(() => {
    generateNewQuestion();
  }, []);

  const generateNewQuestion = () => {
    const a = Math.floor(Math.random() * 10) + 1;
    const b = Math.floor(Math.random() * 10) + 1;
    setNum1(a);
    setNum2(b);
    setUserAnswer('');
    setMessage('');
  };

  const handleSubmit = () => {
    const answer = parseInt(userAnswer);
    const correct = num1 * num2;
    if (answer === correct) {
      setScore(score + 1);
      setMessage('Correct! Success');
      setTimeout(generateNewQuestion, 1500);
    } else {
      setMessage('Try again! 😊');
    }
  };

  return (
    <div className="text-center p-8">
      <h3 className="text-2xl font-bold mb-4">Multiplication Master</h3>
      <p className="mb-6">Multiply the numbers!</p>

      <div className="text-4xl mb-6">
        <span>{num1}</span> × <span>{num2}</span> = <span className="text-blue-600">Loading</span>
      </div>

      <div className="mb-4">
        <input
          type="number"
          value={userAnswer}
          onChange={(e) => setUserAnswer(e.target.value)}
          className="w-20 px-3 py-2 border border-gray-300 rounded-md text-center text-xl"
          placeholder="?"
        />
      </div>

      <button
        onClick={handleSubmit}
        className="bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700 mb-4"
      >
        Submit
      </button>

      <p className="text-lg mb-4">{message}</p>
      <p className="text-xl font-semibold">Score: {score}</p>
    </div>
  );
};

const DivisionChallengeGame = ({ onClose }) => {
  const [num1, setNum1] = useState(0);
  const [num2, setNum2] = useState(0);
  const [userAnswer, setUserAnswer] = useState('');
  const [message, setMessage] = useState('');
  const [score, setScore] = useState(0);

  useEffect(() => {
    generateNewQuestion();
  }, []);

  const generateNewQuestion = () => {
    const divisor = Math.floor(Math.random() * 10) + 1;
    const quotient = Math.floor(Math.random() * 10) + 1;
    const dividend = divisor * quotient;
    setNum1(dividend);
    setNum2(divisor);
    setUserAnswer('');
    setMessage('');
  };

  const handleSubmit = () => {
    const answer = parseInt(userAnswer);
    const correct = Math.floor(num1 / num2);
    if (answer === correct) {
      setScore(score + 1);
      setMessage('Correct! Success');
      setTimeout(generateNewQuestion, 1500);
    } else {
      setMessage('Try again! 😊');
    }
  };

  return (
    <div className="text-center p-8">
      <h3 className="text-2xl font-bold mb-4">Division Challenge</h3>
      <p className="mb-6">Divide the numbers!</p>

      <div className="text-4xl mb-6">
        <span>{num1}</span> ÷ <span>{num2}</span> = <span className="text-blue-600">Loading</span>
      </div>

      <div className="mb-4">
        <input
          type="number"
          value={userAnswer}
          onChange={(e) => setUserAnswer(e.target.value)}
          className="w-20 px-3 py-2 border border-gray-300 rounded-md text-center text-xl"
          placeholder="?"
        />
      </div>

      <button
        onClick={handleSubmit}
        className="bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700 mb-4"
      >
        Submit
      </button>

      <p className="text-lg mb-4">{message}</p>
      <p className="text-xl font-semibold">Score: {score}</p>
    </div>
  );
};

const FractionFunGame = ({ onClose }) => {
  const [fraction, setFraction] = useState({ numerator: 1, denominator: 2 });
  const [userAnswer, setUserAnswer] = useState('');
  const [message, setMessage] = useState('');
  const [score, setScore] = useState(0);

  useEffect(() => {
    generateNewQuestion();
  }, []);

  const generateNewQuestion = () => {
    const num = Math.floor(Math.random() * 5) + 1;
    const den = Math.floor(Math.random() * 5) + 2;
    if (num < den) {
      setFraction({ numerator: num, denominator: den });
    } else {
      setFraction({ numerator: den - 1, denominator: den });
    }
    setUserAnswer('');
    setMessage('');
  };

  const handleSubmit = () => {
    const answer = parseFloat(userAnswer);
    const correct = fraction.numerator / fraction.denominator;
    if (Math.abs(answer - correct) < 0.01) {
      setScore(score + 1);
      setMessage('Correct! Success');
      setTimeout(generateNewQuestion, 1500);
    } else {
      setMessage('Try again! 😊');
    }
  };

  return (
    <div className="text-center p-8">
      <h3 className="text-2xl font-bold mb-4">Fraction Fun</h3>
      <p className="mb-6">What decimal is this fraction equal toLoading</p>

      <div className="text-4xl mb-6">
        <sup>{fraction.numerator}</sup>/<sub>{fraction.denominator}</sub> = <span className="text-blue-600">Loading</span>
      </div>

      <div className="mb-4">
        <input
          type="number"
          step="0.01"
          value={userAnswer}
          onChange={(e) => setUserAnswer(e.target.value)}
          className="w-24 px-3 py-2 border border-gray-300 rounded-md text-center text-xl"
          placeholder="0.00"
        />
      </div>

      <button
        onClick={handleSubmit}
        className="bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700 mb-4"
      >
        Submit
      </button>

      <p className="text-lg mb-4">{message}</p>
      <p className="text-xl font-semibold">Score: {score}</p>
    </div>
  );
};

const ShapeAreaCalculatorGame = ({ onClose }) => {
  const [shape, setShape] = useState('rectangle');
  const [dimensions, setDimensions] = useState({ width: 0, height: 0, radius: 0 });
  const [userAnswer, setUserAnswer] = useState('');
  const [message, setMessage] = useState('');
  const [score, setScore] = useState(0);

  useEffect(() => {
    generateNewQuestion();
  }, []);

  const generateNewQuestion = () => {
    const shapes = ['rectangle', 'triangle', 'circle'];
    const randomShape = shapes[Math.floor(Math.random() * shapes.length)];
    setShape(randomShape);

    let dims = {};
    if (randomShape === 'rectangle') {
      dims = { width: Math.floor(Math.random() * 10) + 1, height: Math.floor(Math.random() * 10) + 1, radius: 0 };
    } else if (randomShape === 'triangle') {
      dims = { width: Math.floor(Math.random() * 10) + 1, height: Math.floor(Math.random() * 10) + 1, radius: 0 };
    } else {
      dims = { width: 0, height: 0, radius: Math.floor(Math.random() * 10) + 1 };
    }
    setDimensions(dims);
    setUserAnswer('');
    setMessage('');
  };

  const calculateArea = () => {
    if (shape === 'rectangle') return dimensions.width * dimensions.height;
    if (shape === 'triangle') return (dimensions.width * dimensions.height) / 2;
    if (shape === 'circle') return Math.PI * dimensions.radius * dimensions.radius;
    return 0;
  };

  const handleSubmit = () => {
    const answer = parseFloat(userAnswer);
    const correct = Math.round(calculateArea() * 100) / 100;
    if (Math.abs(answer - correct) < 0.01) {
      setScore(score + 1);
      setMessage('Correct! Success');
      setTimeout(generateNewQuestion, 1500);
    } else {
      setMessage('Try again! 😊');
    }
  };

  return (
    <div className="text-center p-8">
      <h3 className="text-2xl font-bold mb-4">Shape Area Calculator</h3>
      <p className="mb-6">Calculate the area of this {shape}!</p>

      <div className="text-2xl mb-6">
        {shape === 'rectangle' && (
          <div>Rectangle: {dimensions.width} × {dimensions.height}</div>
        )}
        {shape === 'triangle' && (
          <div>Triangle: base {dimensions.width}, height {dimensions.height}</div>
        )}
        {shape === 'circle' && (
          <div>Circle: radius {dimensions.radius}</div>
        )}
      </div>

      <div className="mb-4">
        <input
          type="number"
          step="0.01"
          value={userAnswer}
          onChange={(e) => setUserAnswer(e.target.value)}
          className="w-24 px-3 py-2 border border-gray-300 rounded-md text-center text-xl"
          placeholder="Area"
        />
      </div>

      <button
        onClick={handleSubmit}
        className="bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700 mb-4"
      >
        Submit
      </button>

      <p className="text-lg mb-4">{message}</p>
      <p className="text-xl font-semibold">Score: {score}</p>
    </div>
  );
};

const TimeTellerGame = ({ onClose }) => {
  const [time, setTime] = useState({ hours: 12, minutes: 0 });
  const [userAnswer, setUserAnswer] = useState('');
  const [message, setMessage] = useState('');
  const [score, setScore] = useState(0);

  useEffect(() => {
    generateNewQuestion();
  }, []);

  const generateNewQuestion = () => {
    const hours = Math.floor(Math.random() * 12) + 1;
    const minutes = Math.floor(Math.random() * 4) * 15; // 0, 15, 30, 45
    setTime({ hours, minutes });
    setUserAnswer('');
    setMessage('');
  };

  const getCorrectTime = () => {
    const hour12 = time.hours > 12 ? time.hours - 12 : time.hours;
    const ampm = time.hours >= 12 ? 'PM' : 'AM';
    const minuteStr = time.minutes === 0 ? '' : `:${time.minutes.toString().padStart(2, '0')}`;
    return `${hour12}${minuteStr} ${ampm}`;
  };

  const handleSubmit = () => {
    if (userAnswer.toUpperCase() === getCorrectTime()) {
      setScore(score + 1);
      setMessage('Correct! Success');
      setTimeout(generateNewQuestion, 1500);
    } else {
      setMessage('Try again! 😊');
    }
  };

  const getHourAngle = () => (time.hours % 12) * 30 + time.minutes * 0.5;
  const getMinuteAngle = () => time.minutes * 6;

  return (
    <div className="text-center p-8">
      <h3 className="text-2xl font-bold mb-4">Time Teller</h3>
      <p className="mb-6">What time does this clock showLoading</p>

      <div className="relative w-48 h-48 mx-auto mb-6 border-4 border-gray-800 rounded-full bg-white">
        {/* Clock numbers */}
        {[12, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11].map((num, i) => (
          <div
            key={num}
            className="absolute text-lg font-bold"
            style={{
              top: `${50 - 40 * Math.cos((i * 30 - 90) * Math.PI / 180)}%`,
              left: `${50 + 40 * Math.sin((i * 30 - 90) * Math.PI / 180)}%`,
              transform: 'translate(-50%, -50%)'
            }}
          >
            {num}
          </div>
        ))}

        {/* Hour hand */}
        <div
          className="absolute w-1 bg-black origin-bottom"
          style={{
            height: '25%',
            top: '25%',
            left: '49.5%',
            transform: `rotate(${getHourAngle()}deg)`
          }}
        />

        {/* Minute hand */}
        <div
          className="absolute w-0.5 bg-black origin-bottom"
          style={{
            height: '35%',
            top: '15%',
            left: '49.5%',
            transform: `rotate(${getMinuteAngle()}deg)`
          }}
        />

        {/* Center dot */}
        <div className="absolute w-3 h-3 bg-black rounded-full top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2" />
      </div>

      <div className="mb-4">
        <input
          type="text"
          value={userAnswer}
          onChange={(e) => setUserAnswer(e.target.value)}
          className="w-32 px-3 py-2 border border-gray-300 rounded-md text-center text-xl"
          placeholder="12:00 PM"
        />
      </div>

      <button
        onClick={handleSubmit}
        className="bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700 mb-4"
      >
        Submit
      </button>

      <p className="text-lg mb-4">{message}</p>
      <p className="text-xl font-semibold">Score: {score}</p>
    </div>
  );
};

const SpellingBeeGame = ({ onClose }) => {
  const words = ['CAT', 'DOG', 'SUN', 'CAR', 'BOOK', 'TREE', 'BALL', 'HOUSE', 'BIRD', 'FISH'];
  const [currentWord, setCurrentWord] = useState('');
  const [userAnswer, setUserAnswer] = useState('');
  const [message, setMessage] = useState('');
  const [score, setScore] = useState(0);

  useEffect(() => {
    generateNewWord();
  }, []);

  const generateNewWord = () => {
    const word = words[Math.floor(Math.random() * words.length)];
    setCurrentWord(word);
    setUserAnswer('');
    setMessage('');
  };

  const handleSubmit = () => {
    if (userAnswer.toUpperCase() === currentWord) {
      setScore(score + 1);
      setMessage('Correct! Success');
      setTimeout(generateNewWord, 1500);
    } else {
      setMessage('Try again! 😊');
    }
  };

  return (
    <div className="text-center p-8">
      <h3 className="text-2xl font-bold mb-4">Spelling Bee</h3>
      <p className="mb-6">Spell this word: <span className="text-2xl font-bold text-blue-600">{currentWord}</span></p>

      <div className="mb-4">
        <input
          type="text"
          value={userAnswer}
          onChange={(e) => setUserAnswer(e.target.value)}
          className="w-32 px-3 py-2 border border-gray-300 rounded-md text-center text-xl uppercase"
          placeholder="Spell it"
          maxLength={currentWord.length}
        />
      </div>

      <button
        onClick={handleSubmit}
        className="bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700 mb-4"
      >
        Submit
      </button>

      <p className="text-lg mb-4">{message}</p>
      <p className="text-xl font-semibold">Score: {score}</p>
    </div>
  );
};

const VocabularyBuilderGame = ({ onClose }) => {
  const words = [
    { word: 'ELEPHANT', definition: 'A large animal with a trunk' },
    { word: 'OCEAN', definition: 'A large body of saltwater' },
    { word: 'MOUNTAIN', definition: 'A large natural elevation of the earth\'s surface' },
    { word: 'LIBRARY', definition: 'A place where books are kept' },
    { word: 'GARDEN', definition: 'An area where plants are grown' }
  ];

  const [currentWord, setCurrentWord] = useState({});
  const [options, setOptions] = useState([]);
  const [userAnswer, setUserAnswer] = useState('');
  const [message, setMessage] = useState('');
  const [score, setScore] = useState(0);

  useEffect(() => {
    generateNewQuestion();
  }, []);

  const generateNewQuestion = () => {
    const word = words[Math.floor(Math.random() * words.length)];
    const wrongWords = words.filter(w => w.word !== word.word).slice(0, 3);
    const allOptions = [word.word, ...wrongWords.map(w => w.word)].sort(() => Math.random() - 0.5);
    setCurrentWord(word);
    setOptions(allOptions);
    setUserAnswer('');
    setMessage('');
  };

  const handleSubmit = () => {
    if (userAnswer === currentWord.word) {
      setScore(score + 1);
      setMessage('Correct! Success');
      setTimeout(generateNewQuestion, 1500);
    } else {
      setMessage('Try again! 😊');
    }
  };

  return (
    <div className="text-center p-8">
      <h3 className="text-2xl font-bold mb-4">Vocabulary Builder</h3>
      <p className="mb-6 italic">"{currentWord.definition}"</p>
      <p className="mb-6">Which word matches this definitionLoading</p>

      <div className="grid grid-cols-2 gap-4 mb-6">
        {options.map((option, index) => (
          <button
            key={index}
            onClick={() => setUserAnswer(option)}
            className={`p-3 border-2 rounded-lg transition duration-300 ${
              userAnswer === option ? 'border-blue-600 bg-blue-100' : 'border-gray-300 hover:border-gray-400'
            }`}
          >
            {option}
          </button>
        ))}
      </div>

      <button
        onClick={handleSubmit}
        disabled={!userAnswer}
        className="bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700 disabled:bg-gray-400 mb-4"
      >
        Submit
      </button>

      <p className="text-lg mb-4">{message}</p>
      <p className="text-xl font-semibold">Score: {score}</p>
    </div>
  );
};

const ReadingComprehensionGame = ({ onClose }) => {
  const stories = [
    {
      text: "Tom has a red ball. He likes to play with it in the park. The ball is big and bounces high.",
      question: "What color is Tom's ball?",
      answer: "RED"
    },
    {
      text: "Sarah went to the zoo. She saw lions, tigers, and monkeys. Her favorite animal was the elephant.",
      question: "What was Sarah's favorite animal?",
      answer: "ELEPHANT"
    },
    {
      text: "Mike rides his bike to school every day. He wears a helmet to stay safe. His school is two blocks away.",
      question: "How does Mike get to school?",
      answer: "BIKE"
    }
  ];

  const [currentStory, setCurrentStory] = useState({});
  const [userAnswer, setUserAnswer] = useState('');
  const [message, setMessage] = useState('');
  const [score, setScore] = useState(0);

  useEffect(() => {
    generateNewQuestion();
  }, []);

  const generateNewQuestion = () => {
    const story = stories[Math.floor(Math.random() * stories.length)];
    setCurrentStory(story);
    setUserAnswer('');
    setMessage('');
  };

  const handleSubmit = () => {
    if (userAnswer.toUpperCase() === currentStory.answer) {
      setScore(score + 1);
      setMessage('Correct! Success');
      setTimeout(generateNewQuestion, 1500);
    } else {
      setMessage('Try again! 😊');
    }
  };

  return (
    <div className="text-center p-8">
      <h3 className="text-2xl font-bold mb-4">Reading Comprehension</h3>

      <div className="bg-gray-50 p-4 rounded-lg mb-6 text-left">
        <p className="text-lg">{currentStory.text}</p>
      </div>

      <p className="mb-6 font-semibold">{currentStory.question}</p>

      <div className="mb-4">
        <input
          type="text"
          value={userAnswer}
          onChange={(e) => setUserAnswer(e.target.value)}
          className="w-64 px-3 py-2 border border-gray-300 rounded-md text-center text-xl uppercase"
          placeholder="Your answer"
        />
      </div>

      <button
        onClick={handleSubmit}
        className="bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700 mb-4"
      >
        Submit
      </button>

      <p className="text-lg mb-4">{message}</p>
      <p className="text-xl font-semibold">Score: {score}</p>
    </div>
  );
};

const RhymingWordsGame = ({ onClose }) => {
  const wordPairs = [
    { word: 'CAT', rhymes: ['HAT', 'BAT', 'MAT'] },
    { word: 'DOG', rhymes: ['LOG', 'FOG', 'FROG'] },
    { word: 'SUN', rhymes: ['FUN', 'RUN', 'GUN'] },
    { word: 'CAR', rhymes: ['STAR', 'FAR', 'BAR'] }
  ];

  const [currentPair, setCurrentPair] = useState({});
  const [options, setOptions] = useState([]);
  const [userAnswer, setUserAnswer] = useState('');
  const [message, setMessage] = useState('');
  const [score, setScore] = useState(0);

  useEffect(() => {
    generateNewQuestion();
  }, []);

  const generateNewQuestion = () => {
    const pair = wordPairs[Math.floor(Math.random() * wordPairs.length)];
    const wrongWords = wordPairs.filter(p => p.word !== pair.word).flatMap(p => p.rhymes).slice(0, 3);
    const allOptions = [pair.rhymes[0], ...wrongWords].sort(() => Math.random() - 0.5);
    setCurrentPair(pair);
    setOptions(allOptions);
    setUserAnswer('');
    setMessage('');
  };

  const handleSubmit = () => {
    if (currentPair.rhymes.includes(userAnswer)) {
      setScore(score + 1);
      setMessage('Correct! Success');
      setTimeout(generateNewQuestion, 1500);
    } else {
      setMessage('Try again! 😊');
    }
  };

  return (
    <div className="text-center p-8">
      <h3 className="text-2xl font-bold mb-4">Rhyming Words</h3>
      <p className="mb-6">Which word rhymes with <span className="font-bold text-blue-600">{currentPair.word}</span>Loading</p>

      <div className="grid grid-cols-2 gap-4 mb-6">
        {options.map((option, index) => (
          <button
            key={index}
            onClick={() => setUserAnswer(option)}
            className={`p-3 border-2 rounded-lg transition duration-300 ${
              userAnswer === option ? 'border-blue-600 bg-blue-100' : 'border-gray-300 hover:border-gray-400'
            }`}
          >
            {option}
          </button>
        ))}
      </div>

      <button
        onClick={handleSubmit}
        disabled={!userAnswer}
        className="bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700 disabled:bg-gray-400 mb-4"
      >
        Submit
      </button>

      <p className="text-lg mb-4">{message}</p>
      <p className="text-xl font-semibold">Score: {score}</p>
    </div>
  );
};

const SentenceBuilderGame = ({ onClose }) => {
  const sentences = [
    { words: ['THE', 'CAT', 'SITS', 'ON', 'MAT'], correct: ['THE', 'CAT', 'SITS', 'ON', 'THE', 'MAT'] },
    { words: ['I', 'LIKE', 'TO', 'EAT', 'APPLE'], correct: ['I', 'LIKE', 'TO', 'EAT', 'AN', 'APPLE'] },
    { words: ['SHE', 'GOES', 'SCHOOL', 'EVERY', 'DAY'], correct: ['SHE', 'GOES', 'TO', 'SCHOOL', 'EVERY', 'DAY'] }
  ];

  const [currentSentence, setCurrentSentence] = useState({});
  const [userOrder, setUserOrder] = useState([]);
  const [message, setMessage] = useState('');
  const [score, setScore] = useState(0);

  useEffect(() => {
    generateNewQuestion();
  }, []);

  const generateNewQuestion = () => {
    const sentence = sentences[Math.floor(Math.random() * sentences.length)];
    setCurrentSentence(sentence);
    setUserOrder([]);
    setMessage('');
  };

  const addWord = (word) => {
    if (!userOrder.includes(word)) {
      setUserOrder([...userOrder, word]);
    }
  };

  const removeWord = (word) => {
    setUserOrder(userOrder.filter(w => w !== word));
  };

  const handleSubmit = () => {
    const userSentence = userOrder.join(' ');
    const correctSentence = currentSentence.correct.join(' ');
    if (userSentence === correctSentence) {
      setScore(score + 1);
      setMessage('Correct! Success');
      setTimeout(generateNewQuestion, 2000);
    } else {
      setMessage('Try again! 😊');
    }
  };

  return (
    <div className="text-center p-8">
      <h3 className="text-2xl font-bold mb-4">Sentence Builder</h3>
      <p className="mb-6">Build a correct sentence using the words below!</p>

      <div className="bg-gray-50 p-4 rounded-lg mb-6 min-h-16">
        <p className="text-lg">{userOrder.join(' ') || 'Click words to build your sentence...'}</p>
      </div>

      <div className="grid grid-cols-3 gap-2 mb-6">
        {currentSentence.words?.map((word, index) => (
          <button
            key={index}
            onClick={() => addWord(word)}
            disabled={userOrder.includes(word)}
            className="p-2 bg-blue-100 hover:bg-blue-200 disabled:bg-gray-300 rounded border"
          >
            {word}
          </button>
        ))}
      </div>

      {userOrder.length > 0 && (
        <div className="mb-4">
          <p className="mb-2">Your sentence words:</p>
          <div className="flex flex-wrap gap-2 justify-center">
            {userOrder.map((word, index) => (
              <button
                key={index}
                onClick={() => removeWord(word)}
                className="px-3 py-1 bg-red-100 hover:bg-red-200 rounded border text-sm"
              >
                {word} ×
              </button>
            ))}
          </div>
        </div>
      )}

      <button
        onClick={handleSubmit}
        disabled={userOrder.length === 0}
        className="bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700 disabled:bg-gray-400 mb-4"
      >
        Submit
      </button>

      <p className="text-lg mb-4">{message}</p>
      <p className="text-xl font-semibold">Score: {score}</p>
    </div>
  );
};

const PatternRecognitionGame = ({ onClose }) => {
  const patterns = [
    { sequence: [1, 2, 3, 4], next: 5 },
    { sequence: [2, 4, 6, 8], next: 10 },
    { sequence: [5, 10, 15, 20], next: 25 },
    { sequence: [3, 6, 9, 12], next: 15 }
  ];

  const [currentPattern, setCurrentPattern] = useState({});
  const [userAnswer, setUserAnswer] = useState('');
  const [message, setMessage] = useState('');
  const [score, setScore] = useState(0);

  useEffect(() => {
    generateNewQuestion();
  }, []);

  const generateNewQuestion = () => {
    const pattern = patterns[Math.floor(Math.random() * patterns.length)];
    setCurrentPattern(pattern);
    setUserAnswer('');
    setMessage('');
  };

  const handleSubmit = () => {
    const answer = parseInt(userAnswer);
    if (answer === currentPattern.next) {
      setScore(score + 1);
      setMessage('Correct! Success');
      setTimeout(generateNewQuestion, 1500);
    } else {
      setMessage('Try again! 😊');
    }
  };

  return (
    <div className="text-center p-8">
      <h3 className="text-2xl font-bold mb-4">Pattern Recognition</h3>
      <p className="mb-6">What number comes next in this patternLoading</p>

      <div className="text-3xl mb-6 font-mono">
        {currentPattern.sequence?.join(', ')}, <span className="text-blue-600">Loading</span>
      </div>

      <div className="mb-4">
        <input
          type="number"
          value={userAnswer}
          onChange={(e) => setUserAnswer(e.target.value)}
          className="w-20 px-3 py-2 border border-gray-300 rounded-md text-center text-xl"
          placeholder="?"
        />
      </div>

      <button
        onClick={handleSubmit}
        className="bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700 mb-4"
      >
        Submit
      </button>

      <p className="text-lg mb-4">{message}</p>
      <p className="text-xl font-semibold">Score: {score}</p>
    </div>
  );
};

const SequenceSolverGame = ({ onClose }) => {
  const sequences = [
    { sequence: [1, 1, 2, 3, 5], next: 8, type: 'Fibonacci' },
    { sequence: [1, 4, 9, 16, 25], next: 36, type: 'Squares' },
    { sequence: [1, 8, 27, 64], next: 125, type: 'Cubes' },
    { sequence: [2, 3, 5, 7, 11], next: 13, type: 'Primes' }
  ];

  const [currentSequence, setCurrentSequence] = useState({});
  const [userAnswer, setUserAnswer] = useState('');
  const [message, setMessage] = useState('');
  const [score, setScore] = useState(0);

  useEffect(() => {
    generateNewQuestion();
  }, []);

  const generateNewQuestion = () => {
    const sequence = sequences[Math.floor(Math.random() * sequences.length)];
    setCurrentSequence(sequence);
    setUserAnswer('');
    setMessage('');
  };

  const handleSubmit = () => {
    const answer = parseInt(userAnswer);
    if (answer === currentSequence.next) {
      setScore(score + 1);
      setMessage('Correct! Success');
      setTimeout(generateNewQuestion, 1500);
    } else {
      setMessage('Try again! 😊');
    }
  };

  return (
    <div className="text-center p-8">
      <h3 className="text-2xl font-bold mb-4">Sequence Solver</h3>
      <p className="mb-6">What number comes next in this {currentSequence.type} sequenceLoading</p>

      <div className="text-3xl mb-6 font-mono">
        {currentSequence.sequence?.join(', ')}, <span className="text-blue-600">Loading</span>
      </div>

      <div className="mb-4">
        <input
          type="number"
          value={userAnswer}
          onChange={(e) => setUserAnswer(e.target.value)}
          className="w-20 px-3 py-2 border border-gray-300 rounded-md text-center text-xl"
          placeholder="?"
        />
      </div>

      <button
        onClick={handleSubmit}
        className="bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700 mb-4"
      >
        Submit
      </button>

      <p className="text-lg mb-4">{message}</p>
      <p className="text-xl font-semibold">Score: {score}</p>
    </div>
  );
};

const MazeNavigatorGame = ({ onClose }) => {
  const [maze, setMaze] = useState([
    [1, 1, 1, 1, 1],
    [1, 0, 0, 0, 1],
    [1, 0, 1, 0, 1],
    [1, 0, 0, 0, 1],
    [1, 1, 1, 1, 1]
  ]);
  const [playerPos, setPlayerPos] = useState({ x: 1, y: 1 });
  const [goalPos, setGoalPos] = useState({ x: 3, y: 3 });
  const [message, setMessage] = useState('');
  const [score, setScore] = useState(0);

  const movePlayer = (dx, dy) => {
    const newX = playerPos.x + dx;
    const newY = playerPos.y + dy;

    if (newX >= 0 && newX < 5 && newY >= 0 && newY < 5 && maze[newY][newX] === 0) {
      setPlayerPos({ x: newX, y: newY });

      if (newX === goalPos.x && newY === goalPos.y) {
        setScore(score + 1);
        setMessage('You reached the goal! Success');
        setTimeout(() => {
          // Generate new maze
          setPlayerPos({ x: 1, y: 1 });
          setGoalPos({ x: 3, y: 3 });
          setMessage('');
        }, 1500);
      }
    }
  };

  return (
    <div className="text-center p-8">
      <h3 className="text-2xl font-bold mb-4">Maze Navigator</h3>
      <p className="mb-6">Help the character reach the goal!</p>

      <div className="inline-block mb-6 border-2 border-gray-800">
        {maze.map((row, y) => (
          <div key={y} className="flex">
            {row.map((cell, x) => (
              <div
                key={x}
                className={`w-12 h-12 border border-gray-400 ${
                  cell === 1 ? 'bg-gray-800' :
                  (x === playerPos.x && y === playerPos.y) ? 'bg-blue-500' :
                  (x === goalPos.x && y === goalPos.y) ? 'bg-green-500' : 'bg-white'
                }`}
              />
            ))}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-3 gap-2 mb-6 max-w-48 mx-auto">
        <div></div>
        <button onClick={() => movePlayer(0, -1)} className="bg-gray-200 hover:bg-gray-300 p-2 rounded">↑</button>
        <div></div>
        <button onClick={() => movePlayer(-1, 0)} className="bg-gray-200 hover:bg-gray-300 p-2 rounded">&lt;</button>
        <div></div>
        <button onClick={() => movePlayer(1, 0)} className="bg-gray-200 hover:bg-gray-300 p-2 rounded">-></button>
        <div></div>
        <button onClick={() => movePlayer(0, 1)} className="bg-gray-200 hover:bg-gray-300 p-2 rounded">↓</button>
        <div></div>
      </div>

      <p className="text-lg mb-4">{message}</p>
      <p className="text-xl font-semibold">Score: {score}</p>
    </div>
  );
};

const CodeBreakerGame = ({ onClose }) => {
  const [secretCode, setSecretCode] = useState('');
  const [userGuess, setUserGuess] = useState('');
  const [message, setMessage] = useState('');
  const [score, setScore] = useState(0);
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    generateNewCode();
  }, []);

  const generateNewCode = () => {
    const code = Math.floor(1000 + Math.random() * 9000).toString();
    setSecretCode(code);
    setUserGuess('');
    setMessage('');
    setAttempts(0);
  };

  const handleSubmit = () => {
    setAttempts(attempts + 1);
    const guess = userGuess;

    if (guess === secretCode) {
      setScore(score + 1);
      setMessage(`Correct! You cracked the code in ${attempts + 1} attempts! Success`);
      setTimeout(generateNewCode, 2000);
    } else {
      let correctPos = 0;
      let correctNum = 0;

      for (let i = 0; i < 4; i++) {
        if (guess[i] === secretCode[i]) {
          correctPos++;
        } else if (secretCode.includes(guess[i])) {
          correctNum++;
        }
      }

      setMessage(`${correctPos} correct position(s), ${correctNum} correct number(s) in wrong position(s)`);
    }
  };

  return (
    <div className="text-center p-8">
      <h3 className="text-2xl font-bold mb-4">Code Breaker</h3>
      <p className="mb-6">Guess the 4-digit secret code!</p>

      <div className="mb-4">
        <input
          type="text"
          value={userGuess}
          onChange={(e) => setUserGuess(e.target.value.replace(/\D/g, '').slice(0, 4))}
          className="w-32 px-3 py-2 border border-gray-300 rounded-md text-center text-xl font-mono"
          placeholder="0000"
          maxLength={4}
        />
      </div>

      <button
        onClick={handleSubmit}
        disabled={userGuess.length !== 4}
        className="bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700 disabled:bg-gray-400 mb-4"
      >
        Guess
      </button>

      <p className="text-lg mb-4">{message}</p>
      <p className="text-xl font-semibold">Score: {score} | Attempts: {attempts}</p>
    </div>
  );
};

const MemoryMatchGame = ({ onClose }) => {
  const [cards, setCards] = useState([]);
  const [flippedCards, setFlippedCards] = useState([]);
  const [matchedCards, setMatchedCards] = useState([]);
  const [message, setMessage] = useState('');
  const [score, setScore] = useState(0);

  useEffect(() => {
    initializeGame();
  }, []);

  const initializeGame = () => {
    const symbols = ['🍎', '🍌', '🍊', '🍇', '🍓', '🍑'];
    const gameCards = [...symbols, ...symbols]
      .sort(() => Math.random() - 0.5)
      .map((symbol, index) => ({ id: index, symbol, isFlipped: false }));

    setCards(gameCards);
    setFlippedCards([]);
    setMatchedCards([]);
    setMessage('');
  };

  const flipCard = (cardId) => {
    if (flippedCards.length === 2 || flippedCards.includes(cardId) || matchedCards.includes(cardId)) {
      return;
    }

    const newFlippedCards = [...flippedCards, cardId];
    setFlippedCards(newFlippedCards);

    if (newFlippedCards.length === 2) {
      const [first, second] = newFlippedCards;
      const firstCard = cards.find(c => c.id === first);
      const secondCard = cards.find(c => c.id === second);

      if (firstCard.symbol === secondCard.symbol) {
        setMatchedCards([...matchedCards, first, second]);
        setMessage('Match! Success');
        setScore(score + 1);

        if (matchedCards.length + 2 === cards.length) {
          setTimeout(() => {
            setScore(score + 1);
            setMessage('You won! All pairs matched! Success');
            setTimeout(initializeGame, 2000);
          }, 1000);
        }
      } else {
        setMessage('Not a match! Try again.');
      }

      setTimeout(() => {
        setFlippedCards([]);
        setMessage('');
      }, 1500);
    }
  };

  return (
    <div className="text-center p-8">
      <h3 className="text-2xl font-bold mb-4">Memory Match</h3>
      <p className="mb-6">Find all the matching pairs!</p>

      <div className="grid grid-cols-4 gap-4 mb-6 max-w-md mx-auto">
        {cards.map((card) => (
          <button
            key={card.id}
            onClick={() => flipCard(card.id)}
            className={`w-16 h-16 border-2 rounded-lg transition duration-300 text-2xl ${
              flippedCards.includes(card.id) || matchedCards.includes(card.id)
                ? 'bg-white border-blue-500' : 'bg-blue-500 border-blue-500 hover:bg-blue-600'
            }`}
            disabled={matchedCards.includes(card.id)}
          >
            {flippedCards.includes(card.id) || matchedCards.includes(card.id) ? card.symbol : '?'}
          </button>
        ))}
      </div>

      <p className="text-lg mb-4">{message}</p>
      <p className="text-xl font-semibold">Score: {score}</p>
    </div>
  );
};

const AnimalSoundsGame = ({ onClose }) => {
  const animals = [
    { name: 'CAT', sound: 'MEOW' },
    { name: 'DOG', sound: 'WOOF' },
    { name: 'COW', sound: 'MOO' },
    { name: 'DUCK', sound: 'QUACK' },
    { name: 'SHEEP', sound: 'BAA' },
    { name: 'FROG', sound: 'RIBBIT' }
  ];

  const [currentAnimal, setCurrentAnimal] = useState({});
  const [options, setOptions] = useState([]);
  const [userAnswer, setUserAnswer] = useState('');
  const [message, setMessage] = useState('');
  const [score, setScore] = useState(0);

  useEffect(() => {
    generateNewQuestion();
  }, []);

  const generateNewQuestion = () => {
    const animal = animals[Math.floor(Math.random() * animals.length)];
    const wrongSounds = animals.filter(a => a.name !== animal.name).map(a => a.sound);
    const allOptions = [animal.sound, ...wrongSounds.slice(0, 2)].sort(() => Math.random() - 0.5);
    setCurrentAnimal(animal);
    setOptions(allOptions);
    setUserAnswer('');
    setMessage('');
  };

  const handleSubmit = () => {
    if (userAnswer === currentAnimal.sound) {
      setScore(score + 1);
      setMessage('Correct! Success');
      setTimeout(generateNewQuestion, 1500);
    } else {
      setMessage('Try again! 😊');
    }
  };

  return (
    <div className="text-center p-8">
      <h3 className="text-2xl font-bold mb-4">Animal Sounds</h3>
      <p className="mb-6">What sound does this animal makeLoading</p>

      <div className="text-6xl mb-6">🐱</div>
      <p className="text-2xl font-bold mb-6">{currentAnimal.name}</p>

      <div className="grid grid-cols-3 gap-4 mb-6">
        {options.map((option, index) => (
          <button
            key={index}
            onClick={() => setUserAnswer(option)}
            className={`p-3 border-2 rounded-lg transition duration-300 ${
              userAnswer === option ? 'border-blue-600 bg-blue-100' : 'border-gray-300 hover:border-gray-400'
            }`}
          >
            {option}
          </button>
        ))}
      </div>

      <button
        onClick={handleSubmit}
        disabled={!userAnswer}
        className="bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700 disabled:bg-gray-400 mb-4"
      >
        Submit
      </button>

      <p className="text-lg mb-4">{message}</p>
      <p className="text-xl font-semibold">Score: {score}</p>
    </div>
  );
};

const ColorMixerGame = ({ onClose }) => {
  const [color1, setColor1] = useState('');
  const [color2, setColor2] = useState('');
  const [userAnswer, setUserAnswer] = useState('');
  const [message, setMessage] = useState('');
  const [score, setScore] = useState(0);

  const colorMixes = {
    'RED+YELLOW': 'ORANGE',
    'YELLOW+BLUE': 'GREEN',
    'BLUE+RED': 'PURPLE',
    'RED+BLUE': 'PURPLE',
    'YELLOW+RED': 'ORANGE',
    'BLUE+YELLOW': 'GREEN'
  };

  useEffect(() => {
    generateNewQuestion();
  }, []);

  const generateNewQuestion = () => {
    const mixes = Object.keys(colorMixes);
    const mix = mixes[Math.floor(Math.random() * mixes.length)];
    const [c1, c2] = mix.split('+');
    setColor1(c1);
    setColor2(c2);
    setUserAnswer('');
    setMessage('');
  };

  const handleSubmit = () => {
    const mixKey = `${color1}+${color2}`;
    const correct = colorMixes[mixKey];
    if (userAnswer.toUpperCase() === correct) {
      setScore(score + 1);
      setMessage('Correct! Success');
      setTimeout(generateNewQuestion, 1500);
    } else {
      setMessage('Try again! 😊');
    }
  };

  return (
    <div className="text-center p-8">
      <h3 className="text-2xl font-bold mb-4">Color Mixer</h3>
      <p className="mb-6">What color do you get when you mix these two colorsLoading</p>

      <div className="flex justify-center items-center mb-6 space-x-4">
        <div className="text-center">
          <div className={`w-16 h-16 rounded-full border-4 border-gray-300 mx-auto mb-2`} style={{ backgroundColor: color1.toLowerCase() }} />
          <p className="font-bold">{color1}</p>
        </div>
        <span className="text-2xl">+</span>
        <div className="text-center">
          <div className={`w-16 h-16 rounded-full border-4 border-gray-300 mx-auto mb-2`} style={{ backgroundColor: color2.toLowerCase() }} />
          <p className="font-bold">{color2}</p>
        </div>
        <span className="text-2xl">=</span>
        <span className="text-2xl">Loading</span>
      </div>

      <div className="mb-4">
        <input
          type="text"
          value={userAnswer}
          onChange={(e) => setUserAnswer(e.target.value)}
          className="w-32 px-3 py-2 border border-gray-300 rounded-md text-center text-xl uppercase"
          placeholder="Color"
        />
      </div>

      <button
        onClick={handleSubmit}
        className="bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700 mb-4"
      >
        Submit
      </button>

      <p className="text-lg mb-4">{message}</p>
      <p className="text-xl font-semibold">Score: {score}</p>
    </div>
  );
};

const SoundRecognitionGame = ({ onClose }) => {
  const instruments = [
    { name: 'PIANO', emoji: '🎹' },
    { name: 'DRUMS', emoji: '🥁' },
    { name: 'GUITAR', emoji: '🎸' },
    { name: 'TRUMPET', emoji: '🎺' },
    { name: 'FLUTE', emoji: '🪈' }
  ];

  const [currentInstrument, setCurrentInstrument] = useState({});
  const [options, setOptions] = useState([]);
  const [userAnswer, setUserAnswer] = useState('');
  const [message, setMessage] = useState('');
  const [score, setScore] = useState(0);

  useEffect(() => {
    generateNewQuestion();
  }, []);

  const generateNewQuestion = () => {
    const instrument = instruments[Math.floor(Math.random() * instruments.length)];
    const wrongInstruments = instruments.filter(i => i.name !== instrument.name);
    const allOptions = [instrument.name, ...wrongInstruments.slice(0, 2).map(i => i.name)].sort(() => Math.random() - 0.5);
    setCurrentInstrument(instrument);
    setOptions(allOptions);
    setUserAnswer('');
    setMessage('');
  };

  const handleSubmit = () => {
    if (userAnswer === currentInstrument.name) {
      setScore(score + 1);
      setMessage('Correct! Success');
      setTimeout(generateNewQuestion, 1500);
    } else {
      setMessage('Try again! 😊');
    }
  };

  return (
    <div className="text-center p-8">
      <h3 className="text-2xl font-bold mb-4">Sound Recognition</h3>
      <p className="mb-6">Which instrument makes this soundLoading</p>

      <div className="text-6xl mb-6">{currentInstrument.emoji}</div>

      <div className="grid grid-cols-3 gap-4 mb-6">
        {options.map((option, index) => (
          <button
            key={index}
            onClick={() => setUserAnswer(option)}
            className={`p-3 border-2 rounded-lg transition duration-300 ${
              userAnswer === option ? 'border-blue-600 bg-blue-100' : 'border-gray-300 hover:border-gray-400'
            }`}
          >
            {option}
          </button>
        ))}
      </div>

      <button
        onClick={handleSubmit}
        disabled={!userAnswer}
        className="bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700 disabled:bg-gray-400 mb-4"
      >
        Submit
      </button>

      <p className="text-lg mb-4">{message}</p>
      <p className="text-xl font-semibold">Score: {score}</p>
    </div>
  );
};

const PlanetExplorerGame = ({ onClose }) => {
  const planets = [
    { name: 'MERCURY', color: 'gray', fact: 'Smallest planet' },
    { name: 'VENUS', color: 'yellow', fact: 'Hottest planet' },
    { name: 'EARTH', color: 'blue', fact: 'Our home planet' },
    { name: 'MARS', color: 'red', fact: 'Red planet' },
    { name: 'JUPITER', color: 'orange', fact: 'Largest planet' },
    { name: 'SATURN', color: 'golden', fact: 'Planet with rings' },
    { name: 'URANUS', color: 'lightblue', fact: 'Ice giant' },
    { name: 'NEPTUNE', color: 'blue', fact: 'Windiest planet' }
  ];

  const [currentPlanet, setCurrentPlanet] = useState({});
  const [options, setOptions] = useState([]);
  const [userAnswer, setUserAnswer] = useState('');
  const [message, setMessage] = useState('');
  const [score, setScore] = useState(0);

  useEffect(() => {
    generateNewQuestion();
  }, []);

  const generateNewQuestion = () => {
    const planet = planets[Math.floor(Math.random() * planets.length)];
    const wrongPlanets = planets.filter(p => p.name !== planet.name);
    const allOptions = [planet.name, ...wrongPlanets.slice(0, 2).map(p => p.name)].sort(() => Math.random() - 0.5);
    setCurrentPlanet(planet);
    setOptions(allOptions);
    setUserAnswer('');
    setMessage('');
  };

  const handleSubmit = () => {
    if (userAnswer === currentPlanet.name) {
      setScore(score + 1);
      setMessage('Correct! Success');
      setTimeout(generateNewQuestion, 1500);
    } else {
      setMessage('Try again! 😊');
    }
  };

  return (
    <div className="text-center p-8">
      <h3 className="text-2xl font-bold mb-4">Planet Explorer</h3>
      <p className="mb-6">Which planet is described as "{currentPlanet.fact}"Loading</p>

      <div className="grid grid-cols-3 gap-4 mb-6">
        {options.map((option, index) => (
          <button
            key={index}
            onClick={() => setUserAnswer(option)}
            className={`p-3 border-2 rounded-lg transition duration-300 ${
              userAnswer === option ? 'border-blue-600 bg-blue-100' : 'border-gray-300 hover:border-gray-400'
            }`}
          >
            {option}
          </button>
        ))}
      </div>

      <button
        onClick={handleSubmit}
        disabled={!userAnswer}
        className="bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700 disabled:bg-gray-400 mb-4"
      >
        Submit
      </button>

      <p className="text-lg mb-4">{message}</p>
      <p className="text-xl font-semibold">Score: {score}</p>
    </div>
  );
};

const WeatherWatcherGame = ({ onClose }) => {
  const weatherTypes = [
    { type: 'SUNNY', emoji: '☀️', description: 'Bright and warm' },
    { type: 'RAINY', emoji: '🌧️', description: 'Wet and cloudy' },
    { type: 'CLOUDY', emoji: '☁️', description: 'Gray and overcast' },
    { type: 'SNOWY', emoji: '❄️', description: 'Cold and white' },
    { type: 'WINDY', emoji: '💨', description: 'Breezy and moving' }
  ];

  const [currentWeather, setCurrentWeather] = useState({});
  const [options, setOptions] = useState([]);
  const [userAnswer, setUserAnswer] = useState('');
  const [message, setMessage] = useState('');
  const [score, setScore] = useState(0);

  useEffect(() => {
    generateNewQuestion();
  }, []);

  const generateNewQuestion = () => {
    const weather = weatherTypes[Math.floor(Math.random() * weatherTypes.length)];
    const wrongTypes = weatherTypes.filter(w => w.type !== weather.type);
    const allOptions = [weather.type, ...wrongTypes.slice(0, 2).map(w => w.type)].sort(() => Math.random() - 0.5);
    setCurrentWeather(weather);
    setOptions(allOptions);
    setUserAnswer('');
    setMessage('');
  };

  const handleSubmit = () => {
    if (userAnswer === currentWeather.type) {
      setScore(score + 1);
      setMessage('Correct! Success');
      setTimeout(generateNewQuestion, 1500);
    } else {
      setMessage('Try again! 😊');
    }
  };

  return (
    <div className="text-center p-8">
      <h3 className="text-2xl font-bold mb-4">Weather Watcher</h3>
      <p className="mb-6">What type of weather is thisLoading</p>

      <div className="text-6xl mb-4">{currentWeather.emoji}</div>
      <p className="mb-6 italic">{currentWeather.description}</p>

      <div className="grid grid-cols-3 gap-4 mb-6">
        {options.map((option, index) => (
          <button
            key={index}
            onClick={() => setUserAnswer(option)}
            className={`p-3 border-2 rounded-lg transition duration-300 ${
              userAnswer === option ? 'border-blue-600 bg-blue-100' : 'border-gray-300 hover:border-gray-400'
            }`}
          >
            {option}
          </button>
        ))}
      </div>

      <button
        onClick={handleSubmit}
        disabled={!userAnswer}
        className="bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700 disabled:bg-gray-400 mb-4"
      >
        Submit
      </button>

      <p className="text-lg mb-4">{message}</p>
      <p className="text-xl font-semibold">Score: {score}</p>
    </div>
  );
};

const LivingNonlivingGame = ({ onClose }) => {
  const objects = [
    { name: 'CAT', type: 'living', emoji: '🐱' },
    { name: 'TREE', type: 'living', emoji: '🌳' },
    { name: 'ROCK', type: 'nonliving', emoji: '🪨' },
    { name: 'CAR', type: 'nonliving', emoji: '🚗' },
    { name: 'FLOWER', type: 'living', emoji: '🌸' },
    { name: 'CHAIR', type: 'nonliving', emoji: '🪑' }
  ];

  const [currentObject, setCurrentObject] = useState({});
  const [userAnswer, setUserAnswer] = useState('');
  const [message, setMessage] = useState('');
  const [score, setScore] = useState(0);

  useEffect(() => {
    generateNewQuestion();
  }, []);

  const generateNewQuestion = () => {
    const object = objects[Math.floor(Math.random() * objects.length)];
    setCurrentObject(object);
    setUserAnswer('');
    setMessage('');
  };

  const handleSubmit = () => {
    if (userAnswer === currentObject.type) {
      setScore(score + 1);
      setMessage('Correct! Success');
      setTimeout(generateNewQuestion, 1500);
    } else {
      setMessage('Try again! 😊');
    }
  };

  return (
    <div className="text-center p-8">
      <h3 className="text-2xl font-bold mb-4">Living vs Non-living</h3>
      <p className="mb-6">Is this object living or non-livingLoading</p>

      <div className="text-6xl mb-4">{currentObject.emoji}</div>
      <p className="text-2xl font-bold mb-6">{currentObject.name}</p>

      <div className="flex justify-center space-x-4 mb-6">
        <button
          onClick={() => setUserAnswer('living')}
          className={`px-6 py-3 border-2 rounded-lg transition duration-300 ${
            userAnswer === 'living' ? 'border-green-600 bg-green-100' : 'border-gray-300 hover:border-green-400'
          }`}
        >
          Living
        </button>
        <button
          onClick={() => setUserAnswer('nonliving')}
          className={`px-6 py-3 border-2 rounded-lg transition duration-300 ${
            userAnswer === 'nonliving' ? 'border-blue-600 bg-blue-100' : 'border-gray-300 hover:border-blue-400'
          }`}
        >
          Non-living
        </button>
      </div>

      <button
        onClick={handleSubmit}
        disabled={!userAnswer}
        className="bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700 disabled:bg-gray-400 mb-4"
      >
        Submit
      </button>

      <p className="text-lg mb-4">{message}</p>
      <p className="text-xl font-semibold">Score: {score}</p>
    </div>
  );
};

export default Games;



